import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Bot, User, Loader2, Sparkles } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { GoogleGenAI } from '@google/genai';

const AIAssistant: React.FC = () => {
    const { config, getAllProducts } = useApp();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<{role: 'user' | 'model', text: string}[]>([
        { role: 'model', text: 'Dzień dobry. Szukasz czegoś konkretnego, czy mam zaproponować coś z nowości?' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Do not initialize globally, we will initialize in handleSend and check API key
    // so it doesn't crash the whole app on load if missing.
    // const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userText = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userText }]);
        setIsLoading(true);

        try {
            // Build the catalog context
            let catalogText = 'Oto lista dostępnych produktów w naszym sklepie:\n\n';
            let productCount = 0;
            
            const allProducts = getAllProducts();
            allProducts.forEach(item => {
                if (item.title) {
                    catalogText += `- Nazwa: ${item.title}\n`;
                    if (item.price && item.price !== "0") catalogText += `  Cena: ${item.price} zł\n`;
                    if (item.description) catalogText += `  Opis: ${item.description}\n`;
                    if (item.attributes) {
                        catalogText += `  Cechy: ${Object.entries(item.attributes).map(([k,v]) => `${k}: ${v}`).join(', ')}\n`;
                    }
                    catalogText += `\n`;
                    productCount++;
                }
            });

            if (productCount === 0) {
                catalogText += 'Obecnie nie mamy żadnych produktów w ofercie.';
            }

            const systemInstruction = `Jesteś pomocnym, uprzejmym i eksperckim doradcą klienta w sklepie internetowym o nazwie "${config.companyName || 'Sklep'}".
Twoim zadaniem jest doradzać klientom w wyborze produktów.
Opieraj swoje rekomendacje *WYŁĄCZNIE* na poniższym katalogu produktów.
Bądź zwięzły, konkretny i naturalny w rozmowie. Unikaj zbyt długich odpowiedzi.
Jeśli klient zapyta o coś, czego nie ma w katalogu, grzecznie poinformuj, że obecnie nie mamy takiego wariantu w ofercie i zasugeruj coś podobnego z listy.

--- KATALOG PRODUKTÓW ---
${catalogText}
---------------------`;

            // For simplicity and robust stateless-like execution, we just use generateContent with the history stitched together, 
            // OR use the `history` property if supported. Let's use `generateContent` directly with contents array.
            
            const contents = messages.slice(1).map(msg => ({
                role: msg.role === 'model' ? 'model' : 'user',
                parts: [{ text: msg.text }]
            }));
            
            contents.push({ role: 'user', parts: [{ text: userText }] });

            const payload = {
                model: 'gemini-3.1-flash-lite',
                contents: contents,
                systemInstruction: {
                    parts: [{text: systemInstruction}]
                },
                generationConfig: {
                    temperature: 0.7
                }
            };

            const responseStream = await fetch('api/generate_ai.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!responseStream.ok) {
                let errText = await responseStream.text();
                throw new Error(`Błąd API: ${responseStream.status} ${errText}`);
            }

            const responseData = await responseStream.json();
            const reply = responseData.candidates?.[0]?.content?.parts?.[0]?.text || 'Przepraszam, wystąpił błąd podczas analizowania zapytania.';
            
            // Add empty message placeholder and find its index
            setIsTyping(true);
            setMessages(prev => {
                const updated = [...prev, { role: 'model' as const, text: '' }];
                return updated;
            });
            
            // Stream word by word
            const words = reply.split(' ');
            let currentText = '';
            for (let i = 0; i < words.length; i++) {
                currentText += (i === 0 ? '' : ' ') + words[i];
                // We use function update to always update the message at our known expected index
                // Wait, since we are doing this sequentially and user can type if isLoading is false,
                // let's just keep isLoading=true so input is disabled, but we hide "Pisze..." if a model text is empty?
                // Actually the easiest is to just find the index from the size of prev before we push.
                // But since setState is async, we'll just update the LAST message if it's from 'model'.
                setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMsg = newMessages[newMessages.length - 1];
                    if (lastMsg && lastMsg.role === 'model') {
                        lastMsg.text = currentText;
                    }
                    return newMessages;
                });
                await new Promise(r => setTimeout(r, 60)); // 60ms per word
            }
            
            setIsTyping(false);
            setIsLoading(false); // turn off the loading indicator AFTER streaming finished

        } catch (error) {
            console.error("AI Assistant Error:", error);
            setMessages(prev => [...prev, { role: 'model', text: 'Przepraszam, wystąpił problem z połączeniem z moim modułem AI.' }]);
            setIsTyping(false);
            setIsLoading(false);
        }
    };

    return (
        <>
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className={`fixed bottom-6 right-6 p-3 rounded-full shadow-lg transition-all duration-300 z-50 flex items-center justify-center border
                    ${isOpen ? 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50' : 'bg-black text-white hover:bg-gray-800'}`}
            >
                {isOpen ? <X size={20} /> : <Bot size={20} />}
                {!isOpen && <Sparkles size={10} className="absolute top-1.5 right-1.5 text-yellow-300" />}
            </button>

            {isOpen && (
                <div className="fixed bottom-20 right-6 w-80 md:w-96 bg-white rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-100 h-[500px] animate-fade-in-up">
                    {/* Header */}
                    <div className="bg-white border-b border-gray-100 p-4 text-gray-900 flex items-center gap-3">
                        <div className="bg-gray-100 p-2 rounded text-black">
                            <Bot size={18} />
                        </div>
                        <div>
                            <h3 className="font-serif font-bold text-sm">Osobisty Doradca</h3>
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest">W czym mogę pomóc?</p>
                        </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="flex-1 p-4 overflow-y-auto bg-[#fcfcfc] flex flex-col gap-4">
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                                <div className={`w-8 h-8 rounded shrink-0 flex items-center justify-center ${msg.role === 'user' ? 'bg-gray-100 border border-gray-200' : 'bg-black text-white'}`}>
                                    {msg.role === 'user' ? <User size={14} className="text-gray-600"/> : <Sparkles size={14} />}
                                </div>
                                <div className={`p-3 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-white border border-gray-200 text-gray-800' : 'bg-gray-50 border border-gray-100 text-gray-800'} rounded-sm max-w-[80%]`}>
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                        {isLoading && !isTyping && (
                            <div className="flex gap-3 flex-row">
                                <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-black text-white">
                                    <Sparkles size={14} />
                                </div>
                                <div className="p-3 bg-gray-50 border border-gray-100 text-gray-800 rounded-sm flex items-center gap-2">
                                    <Loader2 size={14} className="animate-spin text-gray-500" />
                                    <span className="text-[10px] text-gray-400 uppercase tracking-wider">Analizuję...</span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-4 bg-white border-t border-gray-100">
                        <div className="flex bg-gray-50 border border-gray-200 focus-within:border-black focus-within:bg-white transition-all rounded-sm overflow-hidden">
                            <input 
                                type="text" 
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                placeholder="Pytanie do asystenta..."
                                className="flex-1 bg-transparent px-4 py-3 text-sm focus:outline-none"
                            />
                            <button 
                                onClick={handleSend}
                                disabled={isLoading || !input.trim()}
                                className="px-4 text-black hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors flex items-center justify-center"
                            >
                                <MessageSquare size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AIAssistant;
